import { createContext, useContext, useEffect, useState } from 'react';
import { createBrowserClient } from '@supabase/ssr';
import type { User, AuthChangeEvent, Session, SupabaseClient } from '@supabase/supabase-js';

type AuthContextType = {
  user: User | null;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return () => {};
    }

    let client: SupabaseClient | undefined;
    try {
      client = createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
      );

      // Check current session
      const checkSession = async () => {
        try {
          if (!client) return;
          const { data: { session } } = await client.auth.getSession();
          if (session) {
            setUser(session.user);
          }
        } catch (error) {
          console.error('Error checking session:', error);
        } finally {
          setLoading(false);
        }
      };

      checkSession();

      // Set up auth state change listener
      if (client) {
        const { data: { subscription } } = client.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
          if (session) {
            setUser(session.user);
          } else {
            setUser(null);
          }
          setLoading(false);
        });

        return () => {
          subscription.unsubscribe();
        };
      }
    } catch (error) {
      console.error('Error initializing Supabase client:', error);
      setLoading(false);
    }

    return () => {};
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 